import { getFundFamilyTransactionAllocationOverrideLoader } from "gen/endpoints/allocation-rule-lookup/allocation-rule-lookup";
import { getFundFamilyDatesRetrieveLoader } from "gen/endpoints/dates/dates";
import { getFeeBasisCategoriesListLoader } from "gen/endpoints/fee-basis-category/fee-basis-category";
import { getFeeInformationListLoader } from "gen/endpoints/fee-information/fee-information";
import { getFundListLoader } from "gen/endpoints/fund-entities/fund-entities";
import { getGlobalEntityListLoader } from "gen/endpoints/global-entity/global-entity";
import {
  getTransactionCalculationAndFeeBasisListLoader,
  getTransactionCalculationListLoader,
} from "gen/endpoints/transactions/transactions";
import { getWaterfallConfigRetrieveLoader } from "gen/endpoints/waterfall/waterfall";
import i18n from "i18n/config";
import { RouteObject } from "react-router-dom";
import { composeLoaders } from "shared/utils/RoutingUtils";

export const fundFamilySetupFundRoutes: RouteObject = {
  id: "fundFamilySetupFund",
  path: "fund",
  children: [
    {
      id: "fundFamilySetupFundIndex",
      handle: {
        showSidebar: true,
      },
      lazy: async () => {
        const { FundFamilySetupFundPage } = await import(
          "features/fund-family/setup/fund/FundFamilySetupFundPage"
        );
        return { Component: FundFamilySetupFundPage };
      },
      children: [
        {
          id: "fundFamilySetupFundBasic",
          path: "",
          lazy: async () => {
            const { FundFamilySetupFundTabProfile } = await import(
              "features/fund-family/setup/fund/FundFamilySetupFundTabProfile"
            );
            return { Component: FundFamilySetupFundTabProfile };
          },
          handle: {
            title: i18n.t("fundFamily:setup.profile.title"),
          },
        },
        {
          id: "fundFamilySetupFundDates",
          path: "dates",
          lazy: async () => {
            const { FundFamilySetupFundTabDates } = await import(
              "features/fund-family/setup/fund/FundFamilySetupFundTabDates"
            );
            return { Component: FundFamilySetupFundTabDates };
          },
          handle: {
            title: i18n.t("fundFamily:setup.dates.title"),
          },
          loader: getFundFamilyDatesRetrieveLoader(),
        },
        {
          id: "fundFamilySetupFundFeesList",
          path: "fees",
          index: true,
          lazy: async () => {
            const { FundFamilySetupFundTabFees } = await import(
              "features/fund-family/setup/fund/FundFamilySetupFundTabFees"
            );
            return { Component: FundFamilySetupFundTabFees };
          },
          handle: {
            title: i18n.t("fundFamily:setup.fees.title"),
          },
          loader: composeLoaders(
            getFeeInformationListLoader(),
            getFundListLoader(),
          ),
        },
        {
          id: "fundFamilySetupFundWaterfall",
          path: "waterfall",
          lazy: async () => {
            const { FundFamilySetupFundTabWaterfall } = await import(
              "features/fund-family/setup/fund/FundFamilySetupFundTabWaterfall"
            );
            return { Component: FundFamilySetupFundTabWaterfall };
          },
          handle: {
            title: i18n.t("fundFamily:setup.waterfall.title"),
          },
          loader: composeLoaders(
            getWaterfallConfigRetrieveLoader(),
            getFundListLoader(),
            getGlobalEntityListLoader(),
          ),
        },
        {
          id: "fundFamilySetupFundCalculations",
          path: "calculations",
          lazy: async () => {
            const { FundFamilySetupFundTabCalculations } = await import(
              "features/fund-family/setup/fund/FundFamilySetupFundTabCalculations"
            );
            return { Component: FundFamilySetupFundTabCalculations };
          },
          handle: {
            title: i18n.t("fundFamily:setup.calculations.title"),
          },
          loader: composeLoaders(
            getTransactionCalculationListLoader(),
            getTransactionCalculationAndFeeBasisListLoader(),
            getFeeBasisCategoriesListLoader(),
          ),
        },
        {
          id: "fundFamilySetupFundAllocations",
          path: "allocations",
          lazy: async () => {
            const { FundFamilySetupFundTabAllocations } = await import(
              "features/fund-family/setup/fund/allocations/FundFamilySetupFundTabAllocations"
            );
            return { Component: FundFamilySetupFundTabAllocations };
          },
          handle: {
            title: i18n.t("fundFamily:setup.transactionAllocation.title"),
          },
          loader: composeLoaders(
            getFundFamilyTransactionAllocationOverrideLoader(),
          ),
        },
        {
          id: "fundFamilySetupFundTransactionCodes",
          path: "transaction-codes",
          lazy: async () => {
            const { FundFamilySetupFundTabTransactionCodes } = await import(
              "features/fund-family/setup/fund/FundFamilySetupFundTabTransactionCodes"
            );
            return { Component: FundFamilySetupFundTabTransactionCodes };
          },
          handle: {
            title: i18n.t("fundFamily:setup.transactionCodes.title"),
          },
        },
      ],
    },
    {
      id: "fundFamilySetupFundFees",
      path: "fees",
      children: [
        {
          id: "fundFamilySetupFundFeesAdd",
          path: "add",
          lazy: async () => {
            const { FundFamilySetupFundFeesAddPage } = await import(
              "features/fund-family/setup/fund/FundFamilySetupFundFeesAddPage"
            );
            return { Component: FundFamilySetupFundFeesAddPage };
          },
        },
        {
          id: "fundFamilySetupFundFeesView",
          path: ":feeId",
          lazy: async () => {
            const { FundFamilySetupFundFeesPage } = await import(
              "features/fund-family/setup/fund/FundFamilySetupFundFeesPage"
            );
            return { Component: FundFamilySetupFundFeesPage };
          },
        },
        {
          id: "fundFamilySetupFundFeesEdit",
          path: "edit/:feeId",
          lazy: async () => {
            const { FundFamilySetupFundFeesEditPage } = await import(
              "features/fund-family/setup/fund/FundFamilySetupFundFeesEditPage"
            );
            return { Component: FundFamilySetupFundFeesEditPage };
          },
        },
      ],
    },
    {
      id: "calculationCreate",
      path: "calculations/create",
      lazy: async () => {
        const { CalculationCreatePage } = await import(
          "features/calculation/CalculationCreatePage"
        );
        return { Component: CalculationCreatePage };
      },
    },
    {
      id: "calculationEdit",
      path: "calculations/:transactionCalculationId",
      lazy: async () => {
        const { CalculationEditPage } = await import(
          "features/calculation/CalculationEditPage"
        );
        return { Component: CalculationEditPage };
      },
    },
    {
      id: "basisEdit",
      path: "fee-basis/:feeBasisId",
      lazy: async () => {
        const { FeeBasisEditPage } = await import(
          "features/calculation/FeeBasisEditPage"
        );
        return { Component: FeeBasisEditPage };
      },
    },
    {
      id: "transactionCodesEdit",
      path: "transaction-codes/edit",
      lazy: async () => {
        const { TransactionCodesEditPage } = await import(
          "features/fund-family/setup/fund/txcodes/TransactionCodesEditPage"
        );
        return { Component: TransactionCodesEditPage };
      },
    },
  ],
};
